import React, { useEffect, useState, useRef } from 'react';

/**
 * Fabric UI
 */
import { AnimationClassNames } from '@uifabric/styling';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import {
  ActionButton,
  PrimaryButton,
  DefaultButton,
  IconButton,
  CommandBarButton
} from '@fluentui/react/lib/Button';
import {
  CommandBar,
  ICommandBarItemProps
} from '@fluentui/react/lib/CommandBar';
import {
  ConstrainMode,
  IColumn,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode
} from '@fluentui/react/lib/DetailsList';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { SearchBox } from '@fluentui/react/lib/SearchBox';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { DirectionalHint } from '@fluentui/react/lib/Callout';

/**
 * Constants and utilities
 */
import { isEmpty, filter, find } from 'lodash';
import { ChangeEvent } from 'react';


import { sortItems, sortItemsByInt } from '../../Utils/Utils';
import { MemberType, CommunityRole, MemberStatus, ICommunityMember } from '../types';

interface Props {
  brandName: string;
  communityAzureId?: string;
  community?: any;
  isProtected: boolean;
  match: any,
  results: any[],
  allMembers: any[],

  communityActionCreators: any,
  membersActionCreators: any,
  inviteMembersActionCreators: any,
  CommunityMemberPersona: any,
  handleBackClick?() : void,
}

const tooltipCommandBarButton: any = (props: any) => {
  const { defaultRender: DefaultRender = CommandBarButton } = props;
  return (
    <TooltipHost
      content={props.ariaLabel}
      directionalHint={DirectionalHint.topAutoEdge}
    >
      <DefaultRender {...props} />
    </TooltipHost>
  );
};

const CommunityMembersComponent = (props: Props) => {
  const {
    brandName,
    communityAzureId,
    isProtected,
    match,
    results,
    allMembers,
    community,
    communityActionCreators,
    membersActionCreators,
    inviteMembersActionCreators,
    handleBackClick = null,
    CommunityMemberPersona,
  } = props;
  const { communityId } = community || {};
  const _communityAzureId = communityAzureId !== '' ? communityAzureId:match.params['communityAzureId'];

  const [searchWord, setSearchWord] = useState<string>('');
  const [searchCleared, setSearchCleared] = useState<boolean>(false);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [hasUpdated, setHasUpdated] = useState<boolean>(false);
  const [filtersCleared, setFiltersCleared] = useState<boolean>(false);
  const [selectedMemberTypes, setSelectedMemberTypes] = useState<any[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<any[]>([]);
  const [selectedCommunityRoles, setSelectedCommunityRoles] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);

  const filteredItemsRef = useRef<any[]>([]);
  const columnsRef = useRef<any[]>([]);

  const onLoad = async () => {
    if (!communityId && _communityAzureId) {
      await communityActionCreators.requestFetchCommunity(_communityAzureId);
    }

    if ( communityId ) {
      await membersActionCreators.requestFetchMembers(communityId);
    }
  };

  useEffect( () => {
    onLoad().then(null);
  }, [communityId, match?.params, communityAzureId]);

  useEffect( () => {
    filteredItemsRef.current = filteredItems;
  }, [filteredItems]);

  useEffect( () => {
    columnsRef.current = columns;
  }, [columns]);

  useEffect(() => {
    if ( communityId )
      membersActionCreators.requestFetchMembers(communityId);
  }, [results]);

  useEffect(() => {
    setHasUpdated(true);
    setFilteredItems([...allMembers]);
    setColumns(getColumns());
  }, [allMembers]);

  useEffect(() => {
    onUpdate();
  }, [searchWord, filtersCleared, selectedMemberTypes, selectedStatuses, selectedCommunityRoles]);

  const onUpdate = () => {
    if (searchWord === '' && searchCleared) {
      onSearch(searchWord);
      setSearchCleared(false);
    }

    if (
      selectedMemberTypes.length === 0 &&
      selectedStatuses.length === 0 &&
      selectedCommunityRoles.length === 0 &&
      filtersCleared
    ) {
      setFiltersCleared(false);

      if (searchWord !== '') {
        onSearch(searchWord);
      }
    }
  };

  const onFilter = () => {
    return onSearch(searchWord ? searchWord:'');
  }

  const onColumnClick = (ev, column) => {
    let newItems = filteredItemsRef?.current ? [...filteredItemsRef.current]:[];
    if ( newItems.length === 0 ) {
      console.log('No data to sort');
      return;
    }

    const newColumns: IColumn[] = columnsRef?.current ? [...columnsRef.current]:[];
    if ( newItems.length === 0 ) {
      console.error('No column data');
      return;
    }

    // Get current column
    const currColumn = newColumns?.filter((currCol, idx) => {
      return column.key === currCol.key;
    })[0];
    if ( !currColumn ) {
      console.error('No column found from list for sorting. A bug to squash');
      return;
    }

    // Decide direction of sorting
    newColumns.forEach((newCol) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });

    switch (currColumn.fieldName) {
      case 'members':
        break;
      case 'isGuest':
      case 'role':
        newItems = sortItems(
          newItems,
          currColumn.fieldName,
          currColumn.isSortedDescending,
          true
        );
        break;
      case 'inactiveDays':
        newItems = sortItemsByInt(
          newItems,
          currColumn.fieldName,
          currColumn.isSortedDescending
        );
        break;
      default:
        newItems = sortItems(
          newItems,
          currColumn.fieldName,
          currColumn.isSortedDescending
        );
        break;
    }

    setColumns([...newColumns]);
    setFilteredItems([...newItems]);
  };

  const getColumns = () => {
    const _columns: IColumn[] = [
      {
        key: 'fullName',
        name: 'Member name',
        fieldName: 'fullName',
        isResizable: true,
        isMultiline: true,
        minWidth: 250,
        maxWidth: 300,
        onColumnClick: onColumnClick
      },
      {
        key: 'inactiveDays',
        name: 'Status',
        fieldName: 'inactiveDays',
        isResizable: true,
        isMultiline: true,
        minWidth: 100,
        maxWidth: 150,
        onColumnClick: onColumnClick
      },
      {
        key: 'memberType',
        name: 'Member type',
        fieldName: 'memberType',
        isResizable: true,
        isMultiline: true,
        minWidth: 100,
        maxWidth: 150,
        onColumnClick: onColumnClick
      },
      {
        key: 'role',
        name: 'Community role',
        fieldName: 'role',
        isResizable: true,
        isMultiline: true,
        minWidth: 125,
        maxWidth: 125,
        ariaLabel: 'Operations for community type',
        onColumnClick: onColumnClick
      },
      {
        key: 'occupation',
        name: 'Job role',
        fieldName: 'occupation',
        minWidth: 120,
        maxWidth: 120,
        isResizable: true,
        isMultiline: true,
        onColumnClick: onColumnClick
      },
      {
        key: 'department',
        name: 'Department',
        fieldName: 'department',
        minWidth: 220,
        maxWidth: 280,
        isResizable: true,
        isMultiline: true,
        onColumnClick: onColumnClick
      }
    ];

    if (
      !isEmpty(community) &&
      community.isOwner
    ) {
      _columns.push(
        {
          key: 'promote/demote',
          name: 'Promote/Demote',
          fieldName: '',
          isResizable: true,
          isMultiline: true,
          minWidth: 125,
          maxWidth: 125
        },
        {
          key: 'remove',
          name: 'Remove',
          fieldName: '',
          minWidth: 60,
          maxWidth: 60
        }
      );
    }

    return _columns;
  }

  const getColorClass = (communityType: string) => {
    switch (communityType) {
      case 'Open':
        return 'community-open';
      case 'Private':
        return 'community-closed';
      case 'Hidden':
        return 'community-hidden';
      default:
        return 'govTeams-blue';
    }
  };

  const onPromote = (event) => {
    membersActionCreators.requestChangeMember(communityId, event.currentTarget.id, true);
  };

  const onDemote = (event) => {
    membersActionCreators.requestChangeMember(communityId, event.currentTarget.id, false);
  };

  const onRemove = (event) => {
    membersActionCreators.requestDeleteMember(communityId, event.currentTarget.id, true);
  };

  const onRenderItem = (item, index, column) => {
    const fieldContent = item[column.fieldName];
    const key = column.key;

    switch (key) {
      case 'fullName':
        return onRenderName(item);
      case 'promote/demote':
        return onRenderPromoteDemote(item);
      case 'isGuest':
        return !item.isGuest ? 'Government' : 'Guest';
      case 'memberType':
        const memberType = 'Government';

        switch (item.memberType) {
          case MemberType.Guest:
            return MemberType.Guest;
          case MemberType.StateGuest:
            return MemberType.StateGuest;
          case MemberType.StateLicensedAccount:
            return 'State Owner';
          case MemberType.LinkedGuestUser:
            return 'External Owner';
          case MemberType.Partner:
            return 'Partner';
          case MemberType.LAM:
            return 'External';
          default:
            return memberType;
        }
      case 'remove':
        return onRenderRemove(item);
      case 'inactiveDays':
        const status = item.memberStatus;
        return status !== 'Inactive'
          ? status
          : `${status} for ${item.inactiveDays} days`;
      default:
        return fieldContent;
    }
  };

  const onRenderName = (item) => {
    return (
      <CommunityMemberPersona
        community={community}
        item={item}
        size={PersonaSize.size32}
      />
    );
  };

  const onRenderPromoteDemote = (item) => {
    const status = item.memberStatus;

    return (
      <React.Fragment>
        {((item.isGuest && item.linkedAzureId !== null) || !item.isGuest) && (
          <React.Fragment>
            {item.role === CommunityRole.Owner ? (
              <IconButton
                id={item.azureId}
                iconProps={{ iconName: 'Down' }}
                onClick={onDemote}
                disabled={
                  community ? !community.isCreated || !community.isOwner : true
                }
              />
            ) : (
              <React.Fragment>
                {status !== MemberStatus.Inactive &&
                  status !== MemberStatus.PendingLicence && (
                    <IconButton
                      id={item.azureId}
                      iconProps={{ iconName: 'Up' }}
                      onClick={onPromote}
                      disabled={
                        community ? !community.isCreated || !community.isOwner : true
                      }
                    />
                  )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  const onRenderRemove = (item) => {
    return (
      <React.Fragment>
        {item.role === CommunityRole.Member && (
          <IconButton
            className='memberItem-remove'
            id={item.azureId}
            iconProps={{ iconName: 'Cancel' }}
            onClick={onRemove}
            disabled={
              community ? !community.isCreated || !community.isOwner : true
            }
          />
        )}
      </React.Fragment>
    );
  };

  const onSearch = (newSearchWord = '') => {
    let _filteredItems = filter(allMembers, (item) => {
      const memberType =
        (!isEmpty(selectedMemberTypes) &&
          find(selectedMemberTypes, (type) => {
            return item.memberType.toLowerCase() === type.toLowerCase();
          })) ||
        isEmpty(selectedMemberTypes)
          ? true
          : false;

      const memberStatus =
        (!isEmpty(selectedStatuses) &&
          find(selectedStatuses, (status) => {
            return item.memberStatus.toLowerCase() === status.toLowerCase();
          })) ||
        isEmpty(selectedStatuses)
          ? true
          : false;

      const communityRole =
        (!isEmpty(selectedCommunityRoles) &&
          find(selectedCommunityRoles, (role) => {
            return item.role.toLowerCase() === role.toLowerCase();
          })) ||
        isEmpty(selectedCommunityRoles)
          ? true
          : false;

      return memberType && memberStatus && communityRole;
    });

    const _searchWord = newSearchWord;
    if (newSearchWord !== '') {
      const regEx = new RegExp(_searchWord, 'i');
      _filteredItems = _filteredItems
        ? _filteredItems.filter((item: ICommunityMember) => {
          return regEx.test(item.fullName);
        })
        : [];
    }

    setSearchWord(_searchWord);
    setFilteredItems(_filteredItems);
  };

  const getCommandBarFarItems = () => {
    const commandBarItems: ICommandBarItemProps[] = [
      {
        key: 'filter',
        name: 'Filter list',
        iconProps: {
          iconName: 'Filter'
        },
        commandBarButtonAs: tooltipCommandBarButton,
        onClick: () => {
          setIsFilterOpen(!isFilterOpen);
        }
      }
    ];

    if (community.isOwner) {
      commandBarItems.push({
        key: 'invite',
        name: 'Invite members',
        className: 'commandBar-primaryButton',
        disabled: !_communityAzureId,
        iconProps: {
          iconName: 'AddGroup'
        },
        commandBarButtonAs: tooltipCommandBarButton,
        onClick: inviteMembers, // FIXME
      });
    }
    return commandBarItems;
  };

  const getMemberTypeOptions = () => {

    if (isProtected) {
      //GTP
      const memberTypeOptionsGTP: IDropdownOption[] = [
        {
          key: MemberType.FAM,
          text: 'Government'
        }, {
          key: MemberType.LAM,
          text: 'External Member'
        }
      ];
      return memberTypeOptionsGTP;
    } else {
      // GTO
      const memberTypeOptionsGTO: IDropdownOption[] = [
        {
          key: 'Full Access Member',
          text: 'Government'
        },
        {
          key: 'Guest',
          text: 'Guest'
        },
        {
          key: 'External owner',
          text: 'External owner'
        },
        {
          key: 'State guest',
          text: 'State guest'
        },
        {
          key: 'State owner',
          text: 'State owner'
        },
        {
          key: 'Partner',
          text: 'Partner'
        },
      ];

      return memberTypeOptionsGTO;
    }
  }

  const onFilterClear = (event) => {
    setFilteredItems(allMembers);
    setSelectedMemberTypes([]);
    setSelectedStatuses([]);
    setSelectedCommunityRoles([]);
    setFiltersCleared(true);
  };

  const onMemberTypeChange = (
    event: React.FormEvent<HTMLDivElement>,
    item: IDropdownOption
  ): void => {
    const newMemberTypes = [...selectedMemberTypes];
    if (item.selected) {
      newMemberTypes.push(item.key as string);
    } else {
      const currIndex = newMemberTypes.indexOf(item.key as string);
      if (currIndex > -1) {
        newMemberTypes.splice(currIndex, 1);
      }
    }
    setSelectedMemberTypes(newMemberTypes);
  };

  const onStatusChange = (
    event: React.FormEvent<HTMLDivElement>,
    item: IDropdownOption
  ): void => {
    const newStatus = [...selectedStatuses];
    if (item.selected) {
      newStatus.push(item.key as string);
    } else {
      const currIndex = newStatus.indexOf(item.key as string);
      if (currIndex > -1) {
        newStatus.splice(currIndex, 1);
      }
    }
    setSelectedStatuses(newStatus);
  };

  const onCommunityRoleChange = (
    event: React.FormEvent<HTMLDivElement>,
    item: IDropdownOption
  ): void => {
    const newCommunityRoles = [...selectedCommunityRoles];
    if (item.selected) {
      newCommunityRoles.push(item.key as string);
    } else {
      const currIndex = newCommunityRoles.indexOf(item.key as string);
      if (currIndex > -1) {
        newCommunityRoles.splice(currIndex, 1);
      }
    }
    setSelectedCommunityRoles(newCommunityRoles);
  };

  const onReturn = async () => {
    if ( handleBackClick && typeof handleBackClick === 'function' )
      return handleBackClick();

    if (hasUpdated) {
      await communityActionCreators.requestFetchCommunity(communityId);
    }
    communityActionCreators.startEditManage(community);
  };

  const inviteMembers = () => {
    inviteMembersActionCreators.startEditInviteMembers(communityId);
  };

  return (
    <div className='communityManagement-layout communityMembers'>
      {!isEmpty(community) && communityId && (
        <React.Fragment>
          <ActionButton
            className='govTeams-actionButton'
            iconProps={{iconName: 'ChevronLeft'}}
            onClick={onReturn}
          >
            <div className='ms-Button-label'>
              Back to {community.displayName}
            </div>
          </ActionButton>

          <div
            className={`page-header ${getColorClass(community.communityType)}`}
          >
            <h1>{community ? community.displayName : ''}</h1>
            <div className='page-header-status'>
              <div className='communityTypeBadge'>
                {community.totalMembers} members
              </div>
            </div>
          </div>
          <div className='page-cell'>
            <div className='searchRow'>
              <div className='searchBox-col'>
                <div className='searchBox'>
                  <SearchBox
                    placeholder='Search list'
                    value={searchWord}
                    onSearch={(newSearchWord: string) => {
                      onSearch(newSearchWord);
                    }}
                    onChange={(event: ChangeEvent, newSearchWord: string) => {
                      setSearchWord(newSearchWord);
                    }}
                    onClear={(event: any) => {
                      setSearchWord('');
                      setSearchCleared(true);
                      setFilteredItems(allMembers);
                    }}
                  />
                  <PrimaryButton
                    text='Search'
                    onClick={() => {
                      onSearch(searchWord);
                    }}
                  />
                </div>
              </div>
            </div>
            <CommandBar
              className='govTeams-CommandBar'
              items={[]}
              farItems={getCommandBarFarItems()}
              styles={{
                root: {
                  marginTop: 20
                }
              }}
            />
            {isFilterOpen && (
              <div
                className={`filterList ${AnimationClassNames.slideDownIn10}`}
              >
                <div className='filterList-row'>
                  <div className='filterList-heading'>
                    <h2>Filter list</h2>
                  </div>
                  <div className='filterList-dropdown'>
                    <Dropdown
                      placeholder='Select member type'
                      label='Member type'
                      multiSelect={true}
                      onChange={onMemberTypeChange}
                      options={getMemberTypeOptions()}
                      selectedKeys={selectedMemberTypes}
                    />
                  </div>
                  <div className='filterList-dropdown'>
                    <Dropdown
                      placeholder='Select status'
                      label='Status'
                      multiSelect={true}
                      onChange={onStatusChange}
                      options={[
                        {
                          key: 'Active',
                          text: 'Active'
                        },
                        {
                          key: 'Under review',
                          text: 'Under review'
                        },
                        {
                          key: 'Inactive',
                          text: 'Inactive'
                        },
                        {
                          key: 'Pending',
                          text: 'Pending'
                        },
                        {
                          key: 'Licence blocked',
                          text: 'Licence blocked'
                        }
                      ]}
                      selectedKeys={selectedStatuses}
                    />
                  </div>
                  <div className='filterList-dropdown'>
                    <Dropdown
                      placeholder='Select community role'
                      label='Community role'
                      multiSelect={true}
                      onChange={onCommunityRoleChange}
                      options={[
                        {
                          key: 'Owner',
                          text: 'Owner'
                        },
                        {
                          key: 'Member',
                          text: 'Member'
                        }
                      ]}
                      selectedKeys={selectedCommunityRoles}
                    />
                  </div>
                  <div className='filterList-footer'>
                    <DefaultButton onClick={onFilterClear}>
                      Clear
                    </DefaultButton>
                    <PrimaryButton onClick={onFilter}>
                      Filter
                    </PrimaryButton>
                  </div>
                </div>
              </div>
            )}
            <DetailsList
              columns={columns}
              constrainMode={ConstrainMode.horizontalConstrained}
              items={filteredItems}
              layoutMode={DetailsListLayoutMode.justified}
              onRenderItemColumn={onRenderItem}
              selectionMode={SelectionMode.none}
            />
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

export default CommunityMembersComponent;